import { useEffect, useState, type FormEvent } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { Plus, Pencil, Trash2, ImageIcon, ExternalLink } from 'lucide-react'
import { toast } from 'sonner'

import { hero, type HeroSlide } from '../api/endpoints'
import { Button } from '../components/Button'
import { Card } from '../components/Card'
import { DataTable } from '../components/DataTable'
import { Dialog, DialogClose, DialogContent } from '../components/Dialog'
import { FormField } from '../components/FormField'
import { Input } from '../components/Input'
import { PageLoader } from '../components/Spinner'
import { StatusBadge } from '../components/StatusBadge'

export function AdminHeroPage() {
  const [slides, setSlides] = useState<HeroSlide[]>([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<HeroSlide | null>(null)
  const [creating, setCreating] = useState(false)

  function reload() {
    setLoading(true)
    hero
      .list()
      .then((r) => setSlides(r.slides))
      .catch((err) =>
        toast.error('Failed to load hero slides', { description: err.message }),
      )
      .finally(() => setLoading(false))
  }

  useEffect(reload, [])

  async function onDelete(s: HeroSlide) {
    if (!confirm(`Delete hero slide ${s.id}?`)) return
    try {
      await hero.delete(s.id)
      toast.success('Slide deleted')
      reload()
    } catch (err) {
      toast.error('Delete failed', { description: (err as Error).message })
    }
  }

  const columns: ColumnDef<HeroSlide>[] = [
    {
      header: 'Preview',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <img
            src={row.original.imageMobileUrl}
            alt="mobile preview"
            className="size-10 rounded-md object-cover bg-ivory border border-line"
          />
          <img
            src={row.original.imageDesktopUrl}
            alt="desktop preview"
            className="size-10 rounded-md object-cover bg-ivory border border-line"
          />
        </div>
      ),
    },
    {
      header: 'Link',
      cell: ({ row }) => (
        <a
          href={row.original.linkUrl}
          target="_blank"
          rel="noreferrer"
          className="text-xs text-forest hover:underline inline-flex items-center gap-1 max-w-[240px] truncate"
          title={row.original.linkUrl}
        >
          <ExternalLink className="size-3 shrink-0" />
          <span className="truncate">{row.original.linkUrl}</span>
        </a>
      ),
    },
    {
      header: 'Order',
      accessorKey: 'sortOrder',
      cell: ({ getValue }) => (
        <span className="font-mono text-xs">{String(getValue())}</span>
      ),
    },
    {
      header: 'Status',
      cell: ({ row }) => <StatusBadge status={row.original.isActive ? 'paid' : 'cancelled'} />,
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div className="flex items-center gap-1 justify-end">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setEditing(row.original)}
            title="Edit slide"
          >
            <Pencil className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onDelete(row.original)}
            title="Delete slide"
          >
            <Trash2 className="size-4 text-danger" />
          </Button>
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-4 max-w-7xl">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-lg font-semibold text-ink">Hero carousel</h2>
          <p className="text-xs text-slatey">
            Slides that appear on the storefront home page. Each slide has a mobile
            and desktop image variant.
          </p>
        </div>
        <Button onClick={() => setCreating(true)} size="sm">
          <Plus className="size-4" /> New slide
        </Button>
      </div>

      {loading && <PageLoader />}

      {!loading && slides.length === 0 ? (
        <Card className="p-10 text-center space-y-2">
          <ImageIcon className="size-10 mx-auto text-slatey" />
          <p className="text-sm text-ink font-medium">No slides yet</p>
          <p className="text-xs text-slatey">
            Add a slide to populate the hero carousel on the storefront.
          </p>
          <Button onClick={() => setCreating(true)} size="sm" className="mt-2">
            <Plus className="size-4" /> Create first slide
          </Button>
        </Card>
      ) : null}

      {!loading && slides.length > 0 ? (
        <DataTable
          data={slides}
          columns={columns}
          emptyTitle="No slides"
          emptyDescription="Add a slide to populate the hero carousel."
        />
      ) : null}

      {creating ? (
        <HeroSlideDialog
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false)
            reload()
          }}
        />
      ) : null}
      {editing ? (
        <HeroSlideDialog
          slide={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            reload()
          }}
        />
      ) : null}
    </div>
  )
}

function HeroSlideDialog({
  slide,
  onClose,
  onSaved,
}: {
  slide?: HeroSlide
  onClose: () => void
  onSaved: () => void
}) {
  const isEdit = !!slide
  const [linkUrl, setLinkUrl] = useState(slide?.linkUrl ?? '')
  const [sortOrder, setSortOrder] = useState(String(slide?.sortOrder ?? 0))
  const [isActive, setIsActive] = useState(slide?.isActive ?? true)
  // New files picked from the OS file picker. null = "no new file".
  const [mobileFile, setMobileFile] = useState<File | null>(null)
  const [desktopFile, setDesktopFile] = useState<File | null>(null)
  const [saving, setSaving] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!linkUrl.trim()) {
      toast.error('Link URL is required')
      return
    }
    // Validate URL format with the URL constructor — cheaper than
    // sending a malformed request to the server.
    try {
      new URL(linkUrl.trim())
    } catch {
      toast.error('Link URL must be a valid URL')
      return
    }
    if (!isEdit && (!mobileFile || !desktopFile)) {
      toast.error('Both mobile and desktop images are required')
      return
    }

    const fd = new FormData()
    if (mobileFile) fd.append('heroImageMobile', mobileFile)
    if (desktopFile) fd.append('heroImageDesktop', desktopFile)
    fd.append('linkUrl', linkUrl.trim())
    fd.append('sortOrder', sortOrder || '0')
    fd.append('isActive', isActive ? 'true' : 'false')

    setSaving(true)
    try {
      if (isEdit && slide) {
        await hero.update(slide.id, fd)
        toast.success('Slide updated')
      } else {
        await hero.create(fd)
        toast.success('Slide added')
      }
      onSaved()
    } catch (err) {
      toast.error('Save failed', { description: (err as Error).message })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title={isEdit ? `Edit slide #${slide!.id}` : 'New hero slide'}
        description="Two image variants — one shown on small screens, one on large. Each slide is a clickable link."
      >
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FormField
              label="Mobile image (≤ 768px)"
              hint={isEdit ? 'Upload only to replace' : 'Required'}
            >
              <input
                type="file"
                accept="image/webp,image/png,image/jpeg,image/avif"
                onChange={(e) => setMobileFile(e.target.files?.[0] ?? null)}
                className="text-xs"
              />
              {slide ? (
                <img
                  src={slide.imageMobileUrl}
                  alt="current mobile"
                  className="mt-1 size-16 rounded-md object-cover bg-ivory border border-line"
                />
              ) : null}
            </FormField>
            <FormField
              label="Desktop image (≥ 768px)"
              hint={isEdit ? 'Upload only to replace' : 'Required'}
            >
              <input
                type="file"
                accept="image/webp,image/png,image/jpeg,image/avif"
                onChange={(e) => setDesktopFile(e.target.files?.[0] ?? null)}
                className="text-xs"
              />
              {slide ? (
                <img
                  src={slide.imageDesktopUrl}
                  alt="current desktop"
                  className="mt-1 size-16 rounded-md object-cover bg-ivory border border-line"
                />
              ) : null}
            </FormField>
          </div>
          <FormField label="Link URL" required hint="Where tapping the slide navigates (e.g. /shop, a product page, an external URL).">
            <Input
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://example.com/promo"
              required
            />
          </FormField>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Sort order">
              <Input
                type="number"
                min={0}
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
              />
            </FormField>
            <FormField label="Active">
              <label className="flex items-center gap-2 mt-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="size-4 accent-forest rounded"
                />
                <span className="text-sm text-ink">Visible on storefront</span>
              </label>
            </FormField>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <DialogClose asChild>
              <Button variant="outline" type="button">
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="submit"
              loading={saving}
              disabled={!isEdit && (!mobileFile || !desktopFile)}
            >
              {isEdit ? 'Save changes' : 'Create slide'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
